// 八字 AI 详细批注 —— 一次性生成并本地存档（除非删除批注，否则不重复生成）
// 调用一次大模型，把结果按档案永久保存；视图层只读取存档
import { get, set, del } from 'idb-keyval'
import type { BirthProfile, BaziResult } from './bazi'
import { chatJson } from './llm'

export interface BaziReadingSection {
  title: string
  titleEn: string
  body: string
  bodyEn: string
}

export interface BaziReading {
  profileId: string
  createdAt: string
  model: string
  verdict: { zh: string; en: string }
  sections: BaziReadingSection[]
  advice: { zh: string; en: string }[]
}

const READING_PREFIX = 'dayi.reading.v1.'

function readingKey(profileId: string): string {
  return READING_PREFIX + profileId
}

function loadLS<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function saveLS(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* quota exceeded — ignore */
  }
}

export async function loadBaziReading(profileId: string): Promise<BaziReading | null> {
  try {
    const v = await get<BaziReading>(readingKey(profileId))
    if (v && v.profileId === profileId) return v
  } catch {
    /* fall through */
  }
  const ls = loadLS<BaziReading>(readingKey(profileId))
  return ls && ls.profileId === profileId ? ls : null
}

export async function saveBaziReading(reading: BaziReading): Promise<void> {
  saveLS(readingKey(reading.profileId), reading)
  try {
    await set(readingKey(reading.profileId), reading)
  } catch {
    /* ignore */
  }
}

export async function deleteBaziReading(profileId: string): Promise<void> {
  localStorage.removeItem(readingKey(profileId))
  try {
    await del(readingKey(profileId))
  } catch {
    /* ignore */
  }
}

/** 把排盘结果序列化为给大模型的输入上下文 */
export function buildBaziPromptData(bazi: BaziResult, profile: BirthProfile): string {
  const pillar = (p: { gan: string; zhi: string; ganWuXing: string; zhiWuXing: string; hideGan: string[]; shiShenGan: string; shiShenZhi: string[]; naYin: string }, name: string) =>
    `${name}柱 ${p.gan}${p.zhi}（天干${p.gan}五行${p.ganWuXing}，十神${p.shiShenGan}；地支${p.zhi}五行${p.zhiWuXing}，藏干${p.hideGan.join('、')}，十神${p.shiShenZhi.join('、')}；纳音${p.naYin}）`
  const lines: string[] = []
  lines.push(`出生信息：${profile.nickname}，性别${profile.gender === 'male' ? '男' : profile.gender === 'female' ? '女' : '不详'}，公历${profile.year}年${profile.month}月${profile.day}日${profile.timeKnown ? ` ${String(profile.hour).padStart(2, '0')}:${String(profile.minute).padStart(2, '0')}` : '（时辰不详）'}${profile.city ? `，城市${profile.city}` : ''}${profile.timeKnown ? `；真太阳时 ${bazi.trueSolarText}` : ''}`)
  lines.push(pillar(bazi.pillars.year, '年'))
  lines.push(pillar(bazi.pillars.month, '月'))
  lines.push(pillar(bazi.pillars.day, '日'))
  lines.push(pillar(bazi.pillars.hour, '时'))
  lines.push(`日主：${bazi.dayMaster.gan}（五行${bazi.dayMaster.wuxing}），强弱${bazi.dayMaster.strength}（得分${bazi.dayMaster.score}）`)
  lines.push(`喜用神：${bazi.usefulGod}；忌神：${bazi.avoidGod}`)
  lines.push(`五行分布：${Object.entries(bazi.wuxingCount).map(([k, v]) => `${k}${v}`).join('，')}`)
  lines.push(`胎元：${bazi.taiYuan}；命宫：${bazi.mingGong}；旬空：${bazi.xunKong}；${bazi.startYunText}`)
  lines.push(`大运：${bazi.daYun.map((d) => `${d.ganZhi}（${d.startAge}–${d.endAge}岁，${d.startYear}–${d.endYear}年）`).join('；')}`)
  if (bazi.currentDaYun) lines.push(`当前大运：${bazi.currentDaYun.ganZhi}（${bazi.currentDaYun.startYear}–${bazi.currentDaYun.endYear}）`)
  if (profile.focusYear) lines.push(`用户关注的时运年份：${profile.focusYear}年（请重点分析该年流年与所处大运）`)
  return lines.join('\n')
}

const SECTION_KEYS = [
  'overview', 'character', 'career', 'wealth', 'love', 'health', 'dayun', 'current', 'practice',
] as const

const SECTION_FALLBACK_TITLES: Record<(typeof SECTION_KEYS)[number], { zh: string; en: string }> = {
  overview: { zh: '命局总论', en: 'Overview of the chart' },
  character: { zh: '性格心性', en: 'Character & temperament' },
  career: { zh: '事业前程', en: 'Career & calling' },
  wealth: { zh: '财运起伏', en: 'Wealth patterns' },
  love: { zh: '婚恋感情', en: 'Love & relationships' },
  health: { zh: '健康养生', en: 'Health & wellbeing' },
  dayun: { zh: '大运起伏', en: 'Major cycles' },
  current: { zh: '当下时运', en: 'Current fortune' },
  practice: { zh: '修行建议', en: 'Practice & growth' },
}

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : ''
}

/** 生成详细批注（调用一次大模型） */
export async function generateBaziReading(
  profile: BirthProfile,
  bazi: BaziResult,
  locale: 'zh' | 'en',
): Promise<BaziReading> {
  const data = buildBaziPromptData(bazi, profile)
  const system = [
    '你是一位严谨、温和、守正的传统命理师，依据《渊海子平》《三命通会》子平法进行命理批注。',
    '你的任务是给用户的四柱八字做一份【详细、准确、温和、可读】的批注档案，帮助用户自我观照，而不是宿命恐吓或贩卖焦虑。',
    '所有判断必须基于我给出的排盘数据，不得编造或篡改任何干支、大运与年份。不确定之处用“可能、倾向、宜留意”等委婉表述。',
    '禁止给出具体生死年份、不可逆的凶断；涉及健康与重大决策时，务必提示咨询专业人士。',
    '输出必须为合法 JSON，结构如下（字符串字段内不要换行转义问题，直接输出 UTF-8 文本）：',
    '{',
    '  "verdict": { "zh": "一句总评（20字内）", "en": "one-sentence verdict" },',
    '  "sections": [',
    '    { "key": "overview|character|career|wealth|love|health|dayun|current|practice", "title": "中文小标题", "titleEn": "English heading", "zh": "中文正文（120-220字，结合具体十神、五行、大运展开）", "en": "English body (equivalent depth)" }',
    '  ],',
    '  "advice": [ { "zh": "一条可执行建议", "en": "one actionable suggestion" } ]',
    '}',
    'sections 至少包含 overview、character、career、wealth、love、health、dayun 七项；若用户设置关注年份，务必包含 current 项分析该流年；practice 可选。',
    '正文要引用具体的十神、五行、喜用神与忌神、大运干支来支撑判断，并落到“可以怎么做”的指导上。',
  ].join('\n')
  const user = [
    `请为下面的八字写详细批注（语言：${locale === 'zh' ? '中文为主，英文同步' : 'English primary, Chinese synced'}）：`,
    data,
  ].join('\n\n')

  const raw = (await chatJson(system, user, { temperature: 0.5, maxTokens: 6000 })) as {
    verdict?: { zh?: string; en?: string }
    sections?: Array<Record<string, unknown>>
    advice?: Array<{ zh?: string; en?: string }>
  }

  const sections: BaziReadingSection[] = []
  const used = new Set<string>()
  for (const s of raw.sections ?? []) {
    const key = str(s.key) as (typeof SECTION_KEYS)[number]
    if (!SECTION_KEYS.includes(key) || used.has(key)) continue
    const body = str(s.zh)
    const bodyEn = str(s.en)
    if (!body && !bodyEn) continue
    used.add(key)
    sections.push({
      title: str(s.title) || SECTION_FALLBACK_TITLES[key].zh,
      titleEn: str(s.titleEn) || SECTION_FALLBACK_TITLES[key].en,
      body,
      bodyEn,
    })
  }
  for (const key of SECTION_KEYS) {
    if (used.has(key)) continue
    const body = str((raw.sections ?? []).find((s) => str(s.key) === key)?.zh)
    if (body) {
      sections.push({ title: SECTION_FALLBACK_TITLES[key].zh, titleEn: SECTION_FALLBACK_TITLES[key].en, body, bodyEn: '' })
    }
  }

  const advice = (raw.advice ?? []).slice(0, 6).map((a) => ({ zh: str(a.zh), en: str(a.en) })).filter((a) => a.zh || a.en)
  const reading: BaziReading = {
    profileId: profile.id,
    createdAt: new Date().toISOString(),
    model: 'deepseek',
    verdict: { zh: str(raw.verdict?.zh), en: str(raw.verdict?.en) },
    sections,
    advice,
  }
  await saveBaziReading(reading)
  return reading
}
